/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDEditor from "components/MDEditor";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import { Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from "@mui/material";

// NewProduct page components
import FormField from "layouts/ecommerce/products/edit-product/components/FormField";
import { getAllEventApi, getOneEventSiegeApi } from "api/events";
import { getAllEventTitleApi } from "api/events";

const EMPTY_SIEGE_ROW = () => ({ siegeUId: "", numbersOfUse: 0, selectedSiegeItem: null, name: "" });
const EMPTY_DATE_GROUP = () => ({ selectedDateItem: null, siegeRows: [EMPTY_SIEGE_ROW()] });

function FormFields({ formValues }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.only("sm"));

  useEffect(() => {
    getInitialData();
  }, []);

  const [eventData, setEventData] = useState([]);
  const [siegeData, setSiegeData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSiege, setSelectedSiege] = useState(null);
  const [isCinemaOrCirque, setIsCinemaOrCirque] = useState(false);
  const [eventDateOptions, setEventDateOptions] = useState([]);

  // cinema/cirque: one group per date, each group has its own siege rows
  const [dateGroups, setDateGroups] = useState([EMPTY_DATE_GROUP()]);

  const [formDataValues, setFormDataValues] = useState({
    eventId: "",
    coponCode: "",
    reduction: 0,
    fromDate: "",
    toDate: "",
    typeOfCopon: "montant",
  });

  // non-cinema/cirque: flat siege list
  const [siegeFormDataValues, setSiegeFormDataValues] = useState([
    { siegeUId: "", numbersOfUse: 0, selectedSiegeItem: null, selectedTimeId: null, selectedDateItem: null },
  ]);

  const getInitialData = async () => {
    try {
      const eventResponse = await getAllEventTitleApi();
      const eventDataArray = eventResponse.map((el) => ({
        title: el.eventTitle,
        id: el._id,
      }));
      setEventData(eventDataArray);
    } catch (err) {
      console.log("getInitialData", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormDataValues = { ...formDataValues, [name]: value };
    setFormDataValues(newFormDataValues);
    formValues(newFormDataValues);
  };

  // ── Non-cinema/cirque helpers ──────────────────────────────────────────────

  const buildSiegeArray = (values) =>
    values.map((siegeItem) => ({
      id: siegeItem.siegeUId,
      name: siegeItem.name,
      numberOfUse: siegeItem.numbersOfUse,
      ...(siegeItem.selectedTimeId ? { selectedTimeId: siegeItem.selectedTimeId } : {}),
    }));

  const handleSiegeNumberOfUseInputChange = (index, e) => {
    const { value } = e.target;
    const updated = siegeFormDataValues.map((item, i) =>
      i === index ? { ...item, numbersOfUse: parseInt(value, 10) || 0 } : item
    );
    setSiegeFormDataValues(updated);
    setFormDataValues((prev) => ({ ...prev, siege: buildSiegeArray(updated) }));
    formValues({ ...formDataValues, siege: buildSiegeArray(updated) });
  };

  const handleAutoCompleteSiegeChange = (index, event, newValue) => {
    setSiegeFormDataValues((prev) => {
      const updated = [...prev];
      updated[index] = { ...prev[index], siegeUId: newValue?.id || "", name: newValue?.name || "", selectedSiegeItem: newValue };
      const arr = buildSiegeArray(updated);
      setFormDataValues((p) => ({ ...p, siege: arr }));
      formValues({ ...formDataValues, siege: arr });
      return updated;
    });
  };

  const handleAddNewSiege = () => {
    setSiegeFormDataValues([
      ...siegeFormDataValues,
      { siegeUId: "", numbersOfUse: 0, selectedSiegeItem: null, selectedTimeId: null, selectedDateItem: null },
    ]);
  };

  const handleDeleteSiege = (index) => {
    const updated = siegeFormDataValues.filter((_, i) => i !== index);
    setSiegeFormDataValues(updated);
    setFormDataValues((prev) => ({ ...prev, siege: buildSiegeArray(updated) }));
    formValues({ ...formDataValues, siege: buildSiegeArray(updated) });
  };

  // ── Cinema/cirque date-group helpers ──────────────────────────────────────

  const buildSiegeArrayFromGroups = (groups) =>
    groups.flatMap((group) =>
      group.siegeRows.map((row) => ({
        id: row.siegeUId,
        name: row.name,
        numberOfUse: row.numbersOfUse,
        ...(group.selectedDateItem ? { selectedTimeId: group.selectedDateItem.id } : {}),
      }))
    );

  const updateGroupsAndNotify = (updatedGroups) => {
    setDateGroups(updatedGroups);
    const arr = buildSiegeArrayFromGroups(updatedGroups);
    setFormDataValues((prev) => ({ ...prev, siege: arr }));
    formValues({ ...formDataValues, siege: arr });
  };

  const handleDateGroupChange = (groupIndex, newValue) => {
    const updated = dateGroups.map((g, i) =>
      i === groupIndex ? { ...g, selectedDateItem: newValue, siegeRows: [EMPTY_SIEGE_ROW()] } : g
    );
    updateGroupsAndNotify(updated);
  };

  const handleAddDateGroup = () => {
    updateGroupsAndNotify([...dateGroups, EMPTY_DATE_GROUP()]);
  };

  const handleDeleteDateGroup = (groupIndex) => {
    const updated = dateGroups.filter((_, i) => i !== groupIndex);
    updateGroupsAndNotify(updated);
  };

  const handleGroupSiegeChange = (groupIndex, rowIndex, newValue) => {
    const updated = dateGroups.map((g, gi) => {
      if (gi !== groupIndex) return g;
      const rows = g.siegeRows.map((row, ri) =>
        ri === rowIndex
          ? { ...row, siegeUId: newValue?.id || "", name: newValue?.name || "", selectedSiegeItem: newValue }
          : row
      );
      return { ...g, siegeRows: rows };
    });
    updateGroupsAndNotify(updated);
  };

  const handleGroupSiegeNumberOfUseChange = (groupIndex, rowIndex, e) => {
    const val = parseInt(e.target.value, 10) || 0;
    const updated = dateGroups.map((g, gi) => {
      if (gi !== groupIndex) return g;
      const rows = g.siegeRows.map((row, ri) => (ri === rowIndex ? { ...row, numbersOfUse: val } : row));
      return { ...g, siegeRows: rows };
    });
    updateGroupsAndNotify(updated);
  };

  const handleAddSiegeToGroup = (groupIndex) => {
    const updated = dateGroups.map((g, gi) =>
      gi === groupIndex ? { ...g, siegeRows: [...g.siegeRows, EMPTY_SIEGE_ROW()] } : g
    );
    updateGroupsAndNotify(updated);
  };

  const handleDeleteSiegeFromGroup = (groupIndex, rowIndex) => {
    const updated = dateGroups.map((g, gi) => {
      if (gi !== groupIndex) return g;
      return { ...g, siegeRows: g.siegeRows.filter((_, ri) => ri !== rowIndex) };
    });
    updateGroupsAndNotify(updated);
  };

  // ── Event change / clear ──────────────────────────────────────────────────

  const handleAutoCompleteEventChange = async (event, newValue) => {
    try {
      handleClearForm();

      const newFormDataValues = { ...formDataValues, eventId: newValue ? newValue?.id : "" };
      setFormDataValues(newFormDataValues);
      setSelectedEvent(newValue);
      setIsCinemaOrCirque(false);
      setEventDateOptions([]);

      if (newValue?.id) {
        const oneEventDetails = await getOneEventSiegeApi(newValue?.id);
        setSiegeData(oneEventDetails.eventSiege || []);

        const isCinema = oneEventDetails.isCinema || false;
        const isCirque = oneEventDetails.isCirque || false;
        setIsCinemaOrCirque(isCinema || isCirque);

        if (isCinema || isCirque) {
          const dates = (oneEventDetails.eventDateList || []).map((d) => ({
            id: d.generatedId,
            label: new Date(d.eventDate).toLocaleString("fr-FR", {
              timeZone: "UTC",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
          }));
          setEventDateOptions(dates);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleClearForm = () => {
    setFormDataValues({ eventId: "", coponCode: "", reduction: 0, fromDate: "", toDate: "", typeOfCopon: "montant" });
    setSelectedEvent(null);
    setSelectedSiege(null);
    setSiegeData([]);
    setIsCinemaOrCirque(false);
    setEventDateOptions([]);
    setDateGroups([EMPTY_DATE_GROUP()]);
    setSiegeFormDataValues([{ siegeUId: "", numbersOfUse: 0, selectedSiegeItem: null, selectedTimeId: null, selectedDateItem: null }]);
  };

  return (
    <>
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h5">Information</MDTypography>
          <MDBox mt={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  value={selectedEvent}
                  options={eventData}
                  getOptionLabel={(option) => option?.title || ""}
                  onChange={handleAutoCompleteEventChange}
                  renderInput={(params) => <FormField {...params} label="Evénement" />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  type="text"
                  label="Code coupon"
                  name="coponCode"
                  value={formDataValues.coponCode}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDTypography variant="h6" textTransform="capitalize" fontWeight="regular">
                  Type (montant/pourcentage)
                </MDTypography>
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="typeOfCopon-label"
                    name="typeOfCopon"
                    value={formDataValues.typeOfCopon}
                    onChange={handleInputChange}
                  >
                    <FormControlLabel value="montant" control={<Radio />} label="Montant" />
                    <FormControlLabel value="pourcentage" control={<Radio />} label="Pourcentage" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  type="number"
                  label="Réduction"
                  name="reduction"
                  value={formDataValues.reduction}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  type="date"
                  label="Date de début"
                  name="fromDate"
                  value={formDataValues.fromDate}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  type="date"
                  label="Date de fin"
                  name="toDate"
                  value={formDataValues.toDate}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </Card>

      <Card style={{ marginTop: "1rem" }}>
        <MDBox p={3}>
          <MDTypography variant="h5">Sièges</MDTypography>

          {/* ── Cinema/cirque: date groups ─────────────────────────────── */}
          {isCinemaOrCirque && (
            <>
              {dateGroups.map((group, groupIndex) => {
                const siegeOptions = siegeData.filter(
                  (s) => s.fromGeneratedId === group.selectedDateItem?.id
                );
                return (
                  <MDBox
                    key={groupIndex}
                    mt={2}
                    p={2}
                    sx={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}
                  >
                    {/* Date picker row */}
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} sm={5}>
                        <Autocomplete
                          value={group.selectedDateItem}
                          options={eventDateOptions}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(o, v) => o?.id === v?.id}
                          onChange={(e, newValue) => handleDateGroupChange(groupIndex, newValue)}
                          renderInput={(params) => <FormField {...params} label="Date / Séance" />}
                        />
                      </Grid>
                      {dateGroups.length > 1 && (
                        <Grid item xs="auto">
                          <Icon
                            color="error"
                            fontSize="medium"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDeleteDateGroup(groupIndex)}
                          >
                            delete
                          </Icon>
                        </Grid>
                      )}
                    </Grid>

                    {/* Siege rows — only shown once a date is selected */}
                    {group.selectedDateItem &&
                      group.siegeRows.map((row, rowIndex) => (
                        <MDBox mt={1} key={rowIndex}>
                          <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} sm={5}>
                              <Autocomplete
                                value={row.selectedSiegeItem}
                                options={siegeOptions}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                                onChange={(e, newValue) =>
                                  handleGroupSiegeChange(groupIndex, rowIndex, newValue)
                                }
                                renderInput={(params) => <FormField {...params} label="Siège" />}
                              />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <FormField
                                type="number"
                                label="Nombre d'utilisations"
                                name="numberOfUse"
                                value={row.numbersOfUse || ""}
                                onChange={(e) =>
                                  handleGroupSiegeNumberOfUseChange(groupIndex, rowIndex, e)
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              {group.siegeRows.length - 1 === rowIndex ? (
                                <Icon
                                  color="success"
                                  fontSize="medium"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleAddSiegeToGroup(groupIndex)}
                                >
                                  add
                                </Icon>
                              ) : (
                                <Icon
                                  color="error"
                                  fontSize="medium"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleDeleteSiegeFromGroup(groupIndex, rowIndex)}
                                >
                                  delete
                                </Icon>
                              )}
                            </Grid>
                          </Grid>
                        </MDBox>
                      ))}
                  </MDBox>
                );
              })}

              <MDBox mt={2}>
                <MDButton variant="outlined" color="info" size="small" onClick={handleAddDateGroup}>
                  + Ajouter une date
                </MDButton>
              </MDBox>
            </>
          )}

          {/* ── Non-cinema/cirque: flat siege list ────────────────────── */}
          {!isCinemaOrCirque &&
            siegeFormDataValues.map((el, index) => (
              <MDBox mt={1} key={index}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Autocomplete
                      value={el.selectedSiegeItem}
                      options={siegeData}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(o, v) => o?.id === v?.id}
                      onChange={(event, newValue) =>
                        handleAutoCompleteSiegeChange(index, event, newValue)
                      }
                      renderInput={(params) => <FormField {...params} label="Siège" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormField
                      type="number"
                      label="Nombre d'utilisations"
                      name="numberOfUse"
                      value={el.numbersOfUse || ""}
                      onChange={(e) => handleSiegeNumberOfUseInputChange(index, e)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    {siegeFormDataValues.length - 1 === index ? (
                      <Icon
                        color="success"
                        fontSize="medium"
                        style={{ cursor: "pointer", display: "flex", alignItems: "center", height: "100%" }}
                        onClick={() => handleAddNewSiege()}
                      >
                        add
                      </Icon>
                    ) : (
                      <Icon
                        color="error"
                        fontSize="medium"
                        style={{ cursor: "pointer", display: "flex", alignItems: "center", height: "100%" }}
                        onClick={() => handleDeleteSiege(index)}
                      >
                        delete
                      </Icon>
                    )}
                  </Grid>
                </Grid>
              </MDBox>
            ))}
        </MDBox>
      </Card>
    </>
  );
}

FormFields.propTypes = {
  formValues: PropTypes.func.isRequired,
};

export default FormFields;
